'use client'

import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useState } from 'react'
import { LogOut } from 'lucide-react'
import { ThemeToggle } from '@/components/theme-toggle'
import { Button } from '@/components/ui/button'
import { useSession } from '@/hooks/useSession'
import { ADMIN_NAV } from '@/lib/nav'

/**
 * The admin shell: a persistent sidebar on desktop, a disclosure on mobile.
 *
 * A client component because it reads the pathname to mark the current page — which matters more
 * in an admin than on a marketing site, where people navigate deliberately and repeatedly.
 */
export function AdminShell({ children }: { children: React.ReactNode }) {
    const pathname = usePathname()
    const router = useRouter()
    const { email, signOut, isSigningOut } = useSession()
    const [open, setOpen] = useState(false)

    async function handleSignOut() {
        await signOut()
        router.replace('/login')
    }

    return (
        <div className="flex min-h-viewport flex-col lg:flex-row">
            <header className="flex items-center justify-between border-b border-border px-4 py-3 lg:hidden">
                <span className="font-semibold">Admin</span>
                <button
                    type="button"
                    className="rounded-md border border-border px-3 py-1.5 text-sm"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    aria-controls="admin-nav"
                >
                    {open ? 'Close' : 'Menu'}
                </button>
            </header>

            <nav
                id="admin-nav"
                aria-label="Admin"
                className={`border-border bg-muted lg:block lg:w-64 lg:shrink-0 lg:border-r ${open ? 'block border-b' : 'hidden'}`}
            >
                <div className="hidden px-6 py-5 lg:block">
                    <span className="font-semibold tracking-tight">Admin</span>
                </div>
                <ul className="space-y-1 px-3 pb-4 lg:pb-6">
                    {ADMIN_NAV.map((item) => {
                        const active = pathname === item.href
                        return (
                            <li key={item.href}>
                                <Link
                                    href={item.href}
                                    onClick={() => setOpen(false)}
                                    aria-current={active ? 'page' : undefined}
                                    className={`block rounded-lg px-3 py-2 text-sm transition-colors ${
                                        active ? 'bg-accent font-medium text-foreground' : 'text-muted-foreground hover:bg-card hover:text-foreground'
                                    }`}
                                >
                                    {item.label}
                                </Link>
                            </li>
                        )
                    })}
                </ul>
                <div className="border-t border-border px-3 pt-4 pb-4 lg:pb-6">
                    <p className="truncate px-3 text-xs text-muted-foreground" title={email ?? undefined}>{email ?? 'Signed in'}</p>
                    <div className="mt-2 flex items-center gap-1 px-1">
                        <Button variant="ghost" size="sm" className="flex-1 justify-start" pending={isSigningOut} onClick={() => void handleSignOut()}><LogOut /> Sign out</Button>
                        <ThemeToggle />
                    </div>
                </div>
            </nav>

            <main className="flex-1 px-6 py-8 lg:px-10 lg:py-10">{children}</main>
        </div>
    )
}

export function AdminPage({ title, description, action, children }: {
    title: string; description: string; action?: React.ReactNode; children: React.ReactNode
}) {
    return (
        <>
            <header className="flex flex-wrap items-start justify-between gap-4 border-b border-border pb-6">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
                    <p className="mt-1 text-muted-foreground">{description}</p>
                </div>
                {action}
            </header>
            <div className="pt-8">{children}</div>
        </>
    )
}

/**
 * What an admin screen shows before it has data — or before the feature exists.
 *
 * Deliberately explicit about which it is. An empty table that looks broken and an empty table
 * that is simply empty need different words, and guessing wrong sends someone debugging.
 */
export function EmptyState({ title, body, hint }: { title: string; body: string; hint?: string }) {
    return (
        <div className="rounded-xl border border-dashed border-border px-6 py-16 text-center">
            <p className="font-medium">{title}</p>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">{body}</p>
            {hint && <p className="mt-4 font-mono text-xs text-muted-foreground">{hint}</p>}
        </div>
    )
}
